// Money, as integers. Never a float.
//
// The contract stores every amount as `amountMinor`, an INTEGER in minor
// units, because a float amount is a rounding bug waiting to be found by a
// traveller counting their own cash. So this module parses a typed string
// into an integer with string arithmetic and never calls parseFloat, and it
// only ever divides for DISPLAY, after the value is already safely stored.
import { intlLocale, type Locale } from "./i18n";
import { formatNumber } from "./format";

/**
 * How many digits sit after the separator for each currency the prototype
 * offers. IDR is 0 on purpose: the sen has not circulated for decades, the
 * unit an Indonesian traveller actually types and reads is the whole rupiah,
 * and pretending otherwise would make every seeded figure a hundred times
 * too large. Anything not listed falls back to 2, the ISO 4217 default.
 */
const EXPONENTS: Record<string, number> = {
  IDR: 0,
  JPY: 0,
  KRW: 0,
  VND: 0,
  USD: 2,
  EUR: 2,
  SGD: 2,
  MYR: 2,
  THB: 2,
  AUD: 2,
};

export const CURRENCIES = ["IDR", "USD", "SGD", "MYR", "AUD", "EUR", "JPY", "THB"] as const;

export function currencyExponent(currency: string): number {
  return EXPONENTS[currency.toUpperCase()] ?? 2;
}

/**
 * A typed amount to an integer number of minor units, by string surgery.
 *
 * Accepts what an Indonesian keyboard actually produces: "150.000",
 * "150 000", "1.250,50" and "1250.50" all arrive here. Grouping marks are
 * dropped, the LAST separator is treated as the decimal point only when what
 * follows it is shorter than a group of three, and the fraction is padded or
 * truncated to the currency's exponent. Returns null when the input holds no
 * digits at all, so an empty field is never silently saved as zero.
 */
export function parseAmountToMinor(raw: string, currency: string): number | null {
  const exponent = currencyExponent(currency);
  const cleaned = raw.replace(/[^\d.,]/g, "");
  if (!/\d/.test(cleaned)) return null;

  const lastSeparator = Math.max(cleaned.lastIndexOf("."), cleaned.lastIndexOf(","));
  let whole = cleaned;
  let fraction = "";
  if (lastSeparator !== -1) {
    const tail = cleaned.slice(lastSeparator + 1);
    // "150.000" is one hundred and fifty thousand, not 150 and 0 sen.
    if (tail.length > 0 && tail.length < 3 && exponent > 0) {
      whole = cleaned.slice(0, lastSeparator);
      fraction = tail;
    }
  }
  const wholeDigits = whole.replace(/\D/g, "") || "0";
  const fractionDigits = fraction.replace(/\D/g, "").slice(0, exponent).padEnd(exponent, "0");
  const minor = `${wholeDigits}${fractionDigits}`.replace(/^0+(?=\d)/, "");
  const value = Number(minor);
  return Number.isSafeInteger(value) ? value : null;
}

/** Minor units back to an editable major string, for an edit form. */
export function minorToInput(amountMinor: number, currency: string): string {
  const exponent = currencyExponent(currency);
  const sign = amountMinor < 0 ? "-" : "";
  const digits = `${Math.abs(amountMinor)}`.padStart(exponent + 1, "0");
  if (exponent === 0) return `${sign}${digits}`;
  return `${sign}${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`;
}

/**
 * Display only. The integer is already stored, so dividing here cannot
 * corrupt what the traveller saved.
 */
export function formatMoneyMinor(currency: string, amountMinor: number, locale: Locale): string {
  const exponent = currencyExponent(currency);
  const value = amountMinor / 10 ** exponent;
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: "currency",
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(value);
  } catch {
    return `${currency} ${formatNumber(value, locale)}`;
  }
}
